import { axiosInstance } from "@/lib/axios";
import { useQuery } from "@tanstack/react-query";
import { getLoginType, getSecureItem } from "@/utils/secureStorage";

interface Company {
  _id: string;
  name: string;
  primaryEmail: string;
  secondaryEmail?: string;
  phoneNumber: string;
  logo?: string; // Add logo property for company logos
  subscriptionPlan: "standard" | "enterprise" | "free";
  departments?: Array<{ _id: string; name: string }>;
  // Note: The API response may include employees array if the endpoint supports it
  employees?: Array<{
    _id: string;
    fullName: string;
    email: string;
    role: string;
    departmentId: string;
    isActive: boolean;
  }>;
  createdAt: string;
  updatedAt: string;
}

interface CompanyResponse {
  status: string;
  data: {
    document: Company;
  };
}

export const useLoggedInCompany = (enabled: boolean = true) => {
  console.log("🔄 [COMPANY_QUERY] Hook initialized with enabled:", enabled);
  
  const query = useQuery<CompanyResponse>({
    queryKey: ["loggedInCompany"],
    queryFn: async () => {
      console.log("🔄 [COMPANY_QUERY] Query function called - fetching company data...");
      
      // Check if user is a company admin
      const loginType = getLoginType();
      const userRole = getSecureItem("userRole");
      
      console.log("🔄 [COMPANY_QUERY] User info:", {
        loginType,
        userRole,
        enabled
      });

      // Use the already declared variables
      
      if (loginType === "company" || userRole === "company_admin") {
        // Company admin - try company endpoint first
        try {
          console.log("📤 [COMPANY_QUERY] Fetching as company admin...");
          const companyResponse = await axiosInstance.get("/api/v1/companies/get-current-company");
          console.log("✅ [COMPANY_QUERY] Company API response:", {
            status: companyResponse.status,
            companyName: companyResponse.data.name,
            companyId: companyResponse.data.id,
            employeesCount: companyResponse.data.employees?.length || 0
          });
          return companyResponse.data;
        } catch (companyError: any) {
          console.log("⚠️ [COMPANY_QUERY] Company endpoint failed:", companyError.response?.status);
          // Fall back to user endpoint
        }
      }
      
      // For department admins or if company endpoint failed, use user endpoint
      console.log("📤 [COMPANY_QUERY] Fetching user data...");
      const userResponse = await axiosInstance.get("/api/v1/users/get-current-user");
      
      console.log("✅ [COMPANY_QUERY] User API response:", {
        status: userResponse.status,
        userId: userResponse.data.id,
        userName: userResponse.data.name,
        userCompanyId: userResponse.data.companyId
      });
      
      // Return the user data directly since it contains the companyId we need
      return userResponse.data;
    },
    enabled: enabled, // Only run when enabled
    retry: (failureCount, error: any) => {
      // Don't retry on 401 errors to prevent infinite loops
      if (error?.response?.status === 401) {
        console.log("Company API 401 error, not retrying");
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    retryDelay: 1000,
    // Use global defaults for refetching
  });

  console.log("🔄 [COMPANY_QUERY] Query state:", {
    enabled: enabled,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error?.message,
    hasData: !!query.data
  });

  return query;
};
